/**
 * ProcessingStepList
 *
 * Shared component for rendering a list of processing steps with
 * status icons (check/spinner/error/pending). Used by both
 * UploadProcessingView and DocumentDetailContent.
 */

import { CheckCircle2, Loader2, XCircle, Circle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export type ProcessingStepStatus =
  | "pending"
  | "generating"
  | "completed"
  | "failed"
  | "retrying";

export interface ProcessingStep {
  label: string;
  status: ProcessingStepStatus;
}

function StepIcon({ status }: { status: ProcessingStepStatus }) {
  return (
    <AnimatePresence mode="wait">
      {status === "completed" && (
        <motion.div
          key="completed"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 400, damping: 15 }}
          className="shrink-0"
        >
          <CheckCircle2 size={14} className="text-emerald-500" />
        </motion.div>
      )}
      {(status === "generating" || status === "retrying") && (
        <motion.div
          key="loading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="shrink-0"
        >
          <Loader2 size={14} className="text-blue-500 animate-spin" />
        </motion.div>
      )}
      {status === "failed" && (
        <motion.div
          key="failed"
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="shrink-0"
        >
          <XCircle size={14} className="text-red-500" />
        </motion.div>
      )}
      {status === "pending" && (
        <motion.div
          key="pending"
          initial={{ opacity: 0.5 }}
          animate={{ opacity: 1 }}
          className="shrink-0"
        >
          <Circle size={14} className="text-muted" />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function stepStatusLabel(status: ProcessingStepStatus): string {
  if (status === "completed") return "";
  if (status === "generating") return "In progress...";
  if (status === "retrying") return "Retrying...";
  if (status === "failed") return "Failed";
  return "Waiting...";
}

export function ProcessingStepList({ steps }: { steps: ProcessingStep[] }) {
  return (
    <div className="space-y-1.5">
      {steps.map((step) => (
        <div key={step.label} className="flex items-center gap-2">
          <StepIcon status={step.status} />
          <span
            className={`text-xs ${
              step.status === "completed"
                ? "text-secondary line-through"
                : step.status === "failed"
                  ? "text-red-500"
                  : "text-primary"
            }`}
          >
            {step.label}
          </span>
          {step.status !== "completed" && (
            <span className="text-xs text-muted ml-auto">
              {stepStatusLabel(step.status)}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
